import type { EntitlementsDto } from '@osteopro/shared';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { api, unwrap } from '../../lib/api.js';
import { runAuthed } from '../../lib/auth-store.js';
import { planKeys } from '../../lib/query-keys.js';

/**
 * Droits du cabinet actif : l'offre, son état, les six leviers et les deux compteurs.
 *
 * Lus pour **afficher** les verrous, jamais pour les appliquer — chaque route les revérifie. La
 * V1 cachait un bouton et laissait la route ouverte, ce qui n'est pas un quota mais un décor.
 *
 * Le catalogue, lui, ne se lit pas : il vit dans `@osteopro/shared`, d'où le serveur et le front
 * le tirent tous les deux.
 *
 * `enabled` retient la requête tant que l'adresse n'est pas confirmée : la route répondrait 403.
 */
export function useEntitlements(options?: { readonly enabled?: boolean }) {
  return useQuery({
    queryKey: planKeys.entitlements(),
    queryFn: async (): Promise<EntitlementsDto> => {
      const result = await runAuthed(async () => unwrap(await api.api.plans.entitlements.$get()));
      return result.entitlements;
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * Relit l'usage des quotas. À appeler après toute écriture qui change le nombre de dossiers
 * comptés — création, validation, corbeille, restauration, purge, fusion, import : sans quoi la
 * jauge « x sur 25 » garde l'ancien chiffre jusqu'à la péremption du cache.
 */
export function invalidateEntitlements(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: planKeys.entitlements() });
}
